import React, { useState, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import ManufactureForm from "../manufacture/ManufactureForm";
import SupplierForm from "../supplier/SupplierForm";
import MarketedForm from "../marketedBy/MarketedForm";
import { AuthContext } from '../../contexts/AuthContext'; // for org/user info

/* Simple inline Modal used by this form */
const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 min-w-[320px] max-w-lg relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
          aria-label="Close"
        >
          &times;
        </button>
        {children}
      </div>
    </div>
  );
};

const ProductForm = ({ product, onClose, onSaveSuccess }) => {
  const api = useApi();
  const { user, settings } = useContext(AuthContext || {}); // optional

  const [manufacturers, setManufacturers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [categories, setCategories] = useState([]);

  const [showManufacturerForm, setShowManufacturerForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showMarketerForm, setShowMarketerForm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // NEW: top-level tab state ('product' | 'batches')
  const [activeTab, setActiveTab] = useState('product');

  // Batches API state
  const [batchesList, setBatchesList] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesError, setBatchesError] = useState(null);
  const [showNewBatchForm, setShowNewBatchForm] = useState(false);
  const [newBatch, setNewBatch] = useState({
    batchNumber: '',
    expiryDate: '',
    quantity: '',
    purchasePrice: '',
    sellPrice: '',
    note: '',
    storeId: ''
  });

  // Stores for batch store dropdown
  const [stores, setStores] = useState([]);
  
  const emptyForm = {
    name: '',
    description: '',
    price: '',
    stock: '',
    sku: '',
    categoryId: '',
    productType: 'retail', // 'retail' | 'food_beverage' | 'medicine'
    // retail / f&b / extended fields
    unitOfMeasure: 'pcs',
    weightBased: false,
    netWeight: '',
    grossWeight: '',
    isPerishable: false,
    shelfLifeDays: '',
    storageTemp: '',
    allergens: [],
    hsnCode: '',
    images: [],
    primaryImageId: null,
    configurableAttributes: [],
    variations: [],
    manufacturedBy: '',
    supplier: '',
    marketedBy: '',
    // medicine
    isMedicine: false,
    prescriptionRequired: false,
    controlledSubstance: false,
    regulatoryId: '',
    drugLicense: { licenseNumber: '', licenseHolder: '', licenseExpiry: '', issuingAuthority: '' },
    dosageForm: '',
    strength: '',
    composition: '',
    contraindications: '',
    batchRequired: false,
    batches: []
  };

  const [formData, setFormData] = useState(emptyForm);

  // Stock-up UI state
  const [showStockUpModal, setShowStockUpModal] = useState(false);
  const [stockUpLoading, setStockUpLoading] = useState(false);
  const [stockUpError, setStockUpError] = useState(null);

  // summary before final create/save
  const [showSummary, setShowSummary] = useState(false);
  // simple SKU uniqueness lock
  const [skuChecking, setSkuChecking] = useState(false);
  const [skuUniqueError, setSkuUniqueError] = useState(null);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [cats, mRes, sRes, mkRes, storesRes] = await Promise.all([
          api.get('categories', { queryParams: { limit: 1000 } }),
          api.get('productAdditionalInfo/manufactures'),
          api.get('productAdditionalInfo/suppliers'),
          api.get('productAdditionalInfo/marketed-bys'),
          api.get('stores', { queryParams: { limit: 1000 } })
        ]);
        setCategories(cats?.data || cats || []);
        setManufacturers(mRes?.data || mRes || []);
        setSuppliers(sRes?.data || sRes || []);
        setMarketers(mkRes?.data || mkRes || []);
        setStores(storesRes?.data || storesRes || []);
      } catch (err) {
        console.error('Failed to load dropdowns/categories', err);
        setError('Failed to load form dropdowns. ' + (err?.message || ''));
      }
    };
    fetchInitial();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!product) {
      setIsEditing(false);
      setFormData(emptyForm);
      return;
    }
    setIsEditing(true);

    const mappedImages = Array.isArray(product.images)
      ? product.images.map(img => ({
          id: img._id || img.id || uuidv4(),
          url: img.url,
          isPrimary: !!img.isPrimary,
          name: img.name || ''
        }))
      : [];

    const primaryImage = mappedImages.find(i => i.isPrimary) || (mappedImages.length ? mappedImages[0] : null);

    setFormData(prev => ({
      ...prev,
      name: product.name || '',
      description: product.description || '',
      price: product.price !== undefined ? String(product.price) : '',
      stock: product.stock !== undefined ? String(product.stock) : '',
      sku: product.sku || '',
      categoryId: product.categoryId?._id || product.categoryId || '',
      images: mappedImages,
      primaryImageId: primaryImage ? primaryImage.id : null,
      configurableAttributes: Array.isArray(product.configurableAttributes)
        ? product.configurableAttributes.map(attr => ({
            id: uuidv4(),
            name: attr.name,
            values: Array.isArray(attr.values) ? attr.values.map(v => ({ id: uuidv4(), value: v })) : []
          }))
        : [],
      variations: Array.isArray(product.variations)
        ? product.variations.map(v => ({
            id: uuidv4(),
            attributeCombination: Array.isArray(v.attributeCombination) ? v.attributeCombination.map(ac => ({ ...ac })) : [],
            price: v.price !== undefined ? String(v.price) : '',
            stock: v.stock !== undefined ? String(v.stock) : '',
            sku: v.sku || '',
            images: Array.isArray(v.images) ? v.images.map(img => ({ id: img._id || uuidv4(), url: img.url, isPrimary: !!img.isPrimary })) : [],
            primaryImageId: v.primaryImageId || null,
            isActive: v.isActive === undefined ? true : !!v.isActive
          }))
        : [],
      manufacturedBy: product.manufacturedBy || '',
      supplier: product.supplier || '',
      marketedBy: product.marketedBy || '',
      productType: product.productType || (product.isMedicine ? 'medicine' : 'retail'),
      unitOfMeasure: product.unitOfMeasure || 'pcs',
      weightBased: !!product.weightBased,
      netWeight: product.netWeight ?? '',
      grossWeight: product.grossWeight ?? '',
      isPerishable: !!product.isPerishable,
      shelfLifeDays: product.shelfLifeDays ?? '',
      storageTemp: product.storageTemp || '',
      allergens: Array.isArray(product.allergens) ? product.allergens : (product.allergens ? (String(product.allergens).split(',').map(s => s.trim()).filter(Boolean)) : []),
      hsnCode: product.hsnCode || '',
      // medicine fields
      isMedicine: !!product.isMedicine,
      prescriptionRequired: !!product.prescriptionRequired,
      controlledSubstance: !!product.controlledSubstance,
      regulatoryId: product.regulatoryId || '',
      drugLicense: {
        licenseNumber: product.drugLicense?.licenseNumber || '',
        licenseHolder: product.drugLicense?.licenseHolder || '',
        licenseExpiry: product.drugLicense?.licenseExpiry ? new Date(product.drugLicense.licenseExpiry).toISOString().slice(0,10) : '',
        issuingAuthority: product.drugLicense?.issuingAuthority || ''
      },
      dosageForm: product.dosageForm || '',
      strength: product.strength || '',
      composition: product.composition || '',
      contraindications: product.contraindications || '',
      batchRequired: !!product.batchRequired,
      batches: Array.isArray(product.batches)
        ? product.batches.map(b => ({
            id: b.batchNumber || uuidv4(),
            batchNumber: b.batchNumber,
            expiryDate: b.expiryDate ? new Date(b.expiryDate).toISOString().slice(0,10) : '',
            quantity: b.quantity || 0,
            purchasePrice: b.purchasePrice || '',
            note: b.note || ''
          }))
        : []
    }));
    // eslint-disable-next-line
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleDrugLicenseChange = (field, value) => {
    setFormData(prev => ({ ...prev, drugLicense: { ...prev.drugLicense, [field]: value } }));
  };

  const addBatch = () => {
    setFormData(prev => ({ ...prev, batches: [...prev.batches, { id: uuidv4(), batchNumber: '', expiryDate: '', quantity: 0, purchasePrice: '', note: '' }] }));
  };

  const updateBatch = (id, field, value) => {
    setFormData(prev => ({ ...prev, batches: prev.batches.map(b => b.id === id ? { ...b, [field]: value } : b) }));
  };

  const removeBatch = (id) => {
    setFormData(prev => ({ ...prev, batches: prev.batches.filter(b => b.id !== id) }));
  };

  // NEW: handle inline new-batch form input changes
  const handleNewBatchChange = (e) => {
    const { name, value } = e.target;
    setNewBatch(prev => ({ ...prev, [name]: value }));
  };

  // Images
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(file => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = (ev) => res({ id: uuidv4(), url: ev.target.result, isPrimary: false, name: file.name });
      r.onerror = rej;
      r.readAsDataURL(file);
    }));
    Promise.all(readers).then(newImages => {
      setFormData(prev => {
        const images = [...prev.images, ...newImages];
        if (!prev.primaryImageId && images.length) {
          images[0].isPrimary = true;
          return { ...prev, images, primaryImageId: images[0].id };
        }
        return { ...prev, images };
      });
    }).catch(err => console.error('Image read error', err));
    e.target.value = '';
  };

  const handleSetPrimaryImage = (id) => {
    setFormData(prev => {
      const images = prev.images.map(img => ({ ...img, isPrimary: img.id === id }));
      return { ...prev, images, primaryImageId: id };
    });
  };

  const handleRemoveImage = (id) => {
    setFormData(prev => {
      const images = prev.images.filter(img => img.id !== id);
      let primaryImageId = prev.primaryImageId;
      if (primaryImageId === id) {
        primaryImageId = images.length ? images[0].id : null;
        if (primaryImageId) images[0].isPrimary = true;
      }
      return { ...prev, images, primaryImageId };
    });
  };

  // Configurable attributes handlers (minimal)
  const handleAddConfigurableAttribute = () => {
    setFormData(prev => ({ ...prev, configurableAttributes: [...prev.configurableAttributes, { id: uuidv4(), name: '', values: [{ id: uuidv4(), value: '' }] }] }));
  };
  const handleConfigurableAttributeChange = (attrId, field, value) => {
    setFormData(prev => ({ ...prev, configurableAttributes: prev.configurableAttributes.map(attr => attr.id === attrId ? { ...attr, [field]: value } : attr) }));
  };
  const handleAddAttributeValue = (attrId) => {
    setFormData(prev => ({ ...prev, configurableAttributes: prev.configurableAttributes.map(attr => attr.id === attrId ? { ...attr, values: [...attr.values, { id: uuidv4(), value: '' }] } : attr) }));
  };
  const handleAttributeValueChange = (attrId, valueId, value) => {
    setFormData(prev => ({ ...prev, configurableAttributes: prev.configurableAttributes.map(attr => attr.id === attrId ? { ...attr, values: attr.values.map(v => v.id === valueId ? { ...v, value } : v) } : attr) }));
  };
  const handleRemoveAttributeValue = (attrId, valueId) => {
    setFormData(prev => ({ ...prev, configurableAttributes: prev.configurableAttributes.map(attr => attr.id === attrId ? { ...attr, values: attr.values.filter(v => v.id !== valueId) } : attr) }));
  };
  const handleRemoveConfigurableAttribute = (attrId) => {
    setFormData(prev => ({ ...prev, configurableAttributes: prev.configurableAttributes.filter(a => a.id !== attrId) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.price || !formData.stock) {
      setError('Please fill required fields: name, price, stock.');
      return;
    }

    // client-side SKU uniqueness check
    if (formData.sku) {
      try {
        setSkuChecking(true);
        setSkuUniqueError(null);
        const q = await api.get('products', { queryParams: { sku: formData.sku, limit: 2 } });
        const found = q?.data?.data ?? q?.data ?? q ?? [];
        const conflict = Array.isArray(found) && found.some(p => (p._id || p.id) !== (product?._id || product?.id));
        if (conflict) {
          setSkuUniqueError('SKU already exists in the organization. Choose a unique SKU.');
          setSkuChecking(false);
          return;
        }
      } catch (err) {
        // non-fatal — allow submit and let server validate too
        console.warn('SKU check failed', err);
      } finally {
        setSkuChecking(false);
      }
    }

    // show confirmation summary before actual submit
    setShowSummary(true);
  };

  const handleSubmitConfirmed = async () => {
    setShowSummary(false);
    setLoading(true);
    setError(null);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock, 10),
        sku: formData.sku || undefined,
        categoryId: formData.categoryId || undefined,
        productType: formData.productType || undefined,
        unitOfMeasure: formData.unitOfMeasure || undefined,
        weightBased: !!formData.weightBased,
        netWeight: formData.netWeight ? Number(formData.netWeight) : undefined,
        grossWeight: formData.grossWeight ? Number(formData.grossWeight) : undefined,
        isPerishable: !!formData.isPerishable,
        shelfLifeDays: formData.shelfLifeDays ? Number(formData.shelfLifeDays) : undefined,
        storageTemp: formData.storageTemp || undefined,
        allergens: Array.isArray(formData.allergens) ? formData.allergens : undefined,
        hsnCode: formData.hsnCode || undefined,
        configurableAttributes: formData.configurableAttributes
          .filter(attr => attr.name.trim() && attr.values.some(v => v.value.trim()))
          .map(attr => ({ name: attr.name, values: attr.values.map(v => v.value).filter(v => v.trim()) })),
        variations: formData.variations,
        images: formData.images.map(img => ({ url: img.url, isPrimary: !!img.isPrimary, name: img.name })),
        primaryImageId: formData.primaryImageId || undefined,
        manufacturedBy: formData.manufacturedBy || undefined,
        supplier: formData.supplier || undefined,
        marketedBy: formData.marketedBy || undefined,
        isMedicine: !!formData.isMedicine,
        prescriptionRequired: formData.isMedicine ? !!formData.prescriptionRequired : false,
        controlledSubstance: formData.isMedicine ? !!formData.controlledSubstance : false,
        regulatoryId: formData.isMedicine ? formData.regulatoryId || undefined : undefined,
        drugLicense: formData.isMedicine ? {
          licenseNumber: formData.drugLicense.licenseNumber || undefined,
          licenseHolder: formData.drugLicense.licenseHolder || undefined,
          licenseExpiry: formData.drugLicense.licenseExpiry || undefined,
          issuingAuthority: formData.drugLicense.issuingAuthority || undefined
        } : undefined,
        dosageForm: formData.isMedicine ? formData.dosageForm || undefined : undefined,
        strength: formData.isMedicine ? formData.strength || undefined : undefined,
        composition: formData.isMedicine ? formData.composition || undefined : undefined,
        contraindications: formData.isMedicine ? formData.contraindications || undefined : undefined,
        batchRequired: !!formData.batchRequired,
        batches: formData.isMedicine && formData.batches.length ? formData.batches.map(b => ({
          batchNumber: b.batchNumber,
          expiryDate: b.expiryDate || null,
          quantity: Number(b.quantity) || 0,
          purchasePrice: b.purchasePrice ? Number(b.purchasePrice) : undefined,
          note: b.note || undefined
        })) : []
      };

      let saved;
      if (isEditing && product && product._id) {
        const res = await api.put(`products/${product._id}`, payload);
        saved = res?.data ?? res;
      } else {
        const res = await api.post('products', payload);
        saved = res?.data ?? res;
      }

      onSaveSuccess && onSaveSuccess(saved);
      onClose && onClose();
      // after create, open Stock Up modal to allow immediate stocking
      if (!isEditing) {
        // if product created and server returned id, set product and open stock modal
        setTimeout(() => {
          // open stock modal for immediate stock
          setShowStockUpModal(true);
        }, 250);
      }
    } catch (err) {
      console.error('Product save error', err);
      setError(err?.message || 'Failed to save product.');
    } finally {
      setLoading(false);
    }
  };

  // load batches when batches tab opens or product changes
  useEffect(() => {
    if (activeTab !== 'batches') return;
    const fetchBatches = async () => {
      // if product saved (has _id) fetch from server, otherwise use local formData.batches
      if (product && product._id) {
        setBatchesLoading(true);
        setBatchesError(null);
        try {
          const res = await api.get('batches', { queryParams: { productId: product._id, limit: 100 } });
          const data = res?.data?.data ?? res?.data ?? res;
          setBatchesList(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error('Failed to fetch batches', err);
          setBatchesError(err?.message || 'Failed to load batches');
          setBatchesList([]);
        } finally {
          setBatchesLoading(false);
        }
      } else {
        // product not saved yet — show local batches staged in formData
        setBatchesList(Array.isArray(formData.batches) ? formData.batches : []);
      }
    };
    fetchBatches();
    // eslint-disable-next-line
  }, [activeTab, product, formData.batches]);

  const createBatch = async (e) => {
    e && e.preventDefault();
    setBatchesError(null);

    if (!newBatch.batchNumber || !newBatch.quantity || !newBatch.storeId) {
      setBatchesError('Please fill required batch fields: batchNumber, quantity, store.');
      return;
    }

    // Payload for server
    const serverPayload = {
      productId: product && product._id ? product._id : undefined,
      batchNumber: newBatch.batchNumber,
      expiryDate: newBatch.expiryDate || undefined,
      quantity: Number(newBatch.quantity),
      purchasePrice: newBatch.purchasePrice ? Number(newBatch.purchasePrice) : undefined,
      sellPrice: newBatch.sellPrice ? Number(newBatch.sellPrice) : undefined,
      note: newBatch.note || undefined,
      storeId: newBatch.storeId
    };

    // If product is saved, POST to /batches. Otherwise, stage locally in formData.batches.
    if (product && product._id) {
      setBatchesLoading(true);
      try {
        const res = await api.post('batches', serverPayload);
        const created = res?.data?.data?.[0] ?? res?.data ?? res;
        if (created && (created._id || created.id)) {
          setBatchesList(prev => [created, ...prev]);
        } else {
          // fallback re-fetch
          const r2 = await api.get('batches', { queryParams: { productId: product._id, limit: 100 } });
          const data = r2?.data?.data ?? r2?.data ?? r2;
          setBatchesList(Array.isArray(data) ? data : []);
        }
        setShowNewBatchForm(false);
        setNewBatch({ batchNumber: '', expiryDate: '', quantity: '', purchasePrice: '', sellPrice: '', note: '', storeId: '' });
      } catch (err) {
        console.error('Failed to create batch', err);
        setBatchesError(err?.message || 'Failed to create batch');
      } finally {
        setBatchesLoading(false);
      }
    } else {
      // Stage locally (will be included in product payload on save)
      const staged = {
        id: uuidv4(),
        batchNumber: newBatch.batchNumber,
        expiryDate: newBatch.expiryDate || '',
        quantity: Number(newBatch.quantity),
        purchasePrice: newBatch.purchasePrice ? Number(newBatch.purchasePrice) : '',
        sellPrice: newBatch.sellPrice ? Number(newBatch.sellPrice) : '',
        note: newBatch.note || '',
        storeId: newBatch.storeId
      };
      setFormData(prev => ({ ...prev, batches: [staged, ...(prev.batches || [])] }));
      setBatchesList(prev => [staged, ...(prev || [])]);
      setShowNewBatchForm(false);
      setNewBatch({ batchNumber: '', expiryDate: '', quantity: '', purchasePrice: '', sellPrice: '', note: '', storeId: '' });
    }
  };

  /* Stock Up Modal + handler */
  const StockUpModal = ({ open, onClose }) => {
    const [qty, setQty] = useState('');
    const [locationId, setLocationId] = useState('');
    const [batchNumber, setBatchNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [purchasePrice, setPurchasePrice] = useState('');
    const [sellPrice, setSellPrice] = useState('');
    const [remarks, setRemarks] = useState('Stocked up new product');

    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg shadow-lg p-6 min-w-[320px] max-w-lg">
          <h3 className="text-lg font-semibold mb-3">Stock Up Product</h3>
          {stockUpError && <div className="text-sm text-red-600 mb-2">{stockUpError}</div>}
          <div className="space-y-2">
            <div>
              <label className="text-sm">Quantity to Add <span title="Must be a positive integer" className="text-gray-400">?</span></label>
              <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 block w-full border rounded p-2" />
            </div>
            <div>
              <label className="text-sm">Store / Warehouse</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="mt-1 block w-full border rounded p-2">
                <option value="">Select location</option>
                {stores.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name || s.storeName || s.code}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm">Batch No (optional)</label>
                <input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} className="mt-1 block w-full border rounded p-2" />
              </div>
              <div>
                <label className="text-sm">Expiry (optional)</label>
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="mt-1 block w-full border rounded p-2" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm">Purchase Price (optional)</label>
                <input type="number" step="0.01" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} className="mt-1 block w-full border rounded p-2" />
              </div>
              <div>
                <label className="text-sm">Sell Price (optional)</label>
                <input type="number" step="0.01" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} className="mt-1 block w-full border rounded p-2" />
              </div>
            </div>
            <div>
              <label className="text-sm">Remarks</label>
              <input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 block w-full border rounded p-2" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
            <button
              type="button"
              disabled={stockUpLoading}
              onClick={async () => {
                setStockUpError(null);
                if (!qty || Number(qty) <= 0) { setStockUpError('Quantity must be a positive number'); return; }
                if (!locationId) { setStockUpError('Select a location'); return; }
                setStockUpLoading(true);
                try {
                  // 1) call stock-adjustment endpoint (server should return updatedStock)
                  const adjustPayload = {
                    productId: product?._id || product?.id,
                    quantity: Number(qty),
                    locationId,
                    batchNumber: batchNumber || undefined,
                    expiryDate: expiryDate || undefined,
                    purchasePrice: purchasePrice !== '' ? Number(purchasePrice) : undefined,
                    sellPrice: sellPrice !== '' ? Number(sellPrice) : undefined,
                    type: 'stock_in', // semantic
                    remarks
                  };
                  const prodId = product?._id || product?.id;
                  const adjRes = await api.post(`products/${prodId}/stock`, adjustPayload);
                  const adj = adjRes?.data ?? adjRes;

                  // 2) create inventory log entry (server may do this automatically; redundant-safe)
                  // const logPayload = {
                  //   productId: product?._id || product?.id,
                  //   organizationId: settings?.organizationId || user?.organizationId,
                  //   quantity: Number(qty),
                  //   locationId,
                  //   resultingStock: adj?.resultingStock ?? null,
                  //   action: 'Stock Up',
                  //   remarks
                  // };
                  // await api.post('inventory/logs', logPayload);

                  // 3) update local batches list if batchNumber provided
                  // update local form stock if server returned updated value
                  if (adj?.resultingStock != null) {
                    setFormData(prev => ({ ...prev, stock: String(adj.resultingStock) }));
                  } else {
                    // simply add to current stock locally
                    setFormData(prev => ({ ...prev, stock: String((Number(prev.stock || 0) + Number(qty)) || 0) }));
                  }

                  setShowStockUpModal(false);
                } catch (err) {
                  console.error('Stock up failed', err);
                  setStockUpError(err?.message || 'Failed to stock up product');
                } finally {
                  setStockUpLoading(false);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              {stockUpLoading ? 'Saving...' : 'Stock Up'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} />}

        {/* Top-level tabs - pill style */}
        <div className="flex items-center gap-3 bg-white shadow-sm rounded-full p-1 inline-flex">
          <button
            type="button"
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${activeTab === 'product' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('product')}
            aria-pressed={activeTab === 'product'}
          >
            Product
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${activeTab === 'batches' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('batches')}
            aria-pressed={activeTab === 'batches'}
          >
            Batches
          </button>
        </div>

        {/* MAIN CARD */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          {/* PRODUCT TAB: all main product fields */}
          {activeTab === 'product' && (
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Product Name</label>
                <input name="name" value={formData.name} onChange={handleChange} required
                  className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description (Optional)</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="3"
                  className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Price</label>
                  <input name="price" value={formData.price} onChange={handleChange} type="number" step="0.01" required
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Stock</label>
                  <input name="stock" value={formData.stock} onChange={handleChange} type="number" min="0" required
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-xs font-semibold text-gray-500 uppercase">SKU (Optional)</label>
                    <div className="group relative flex items-center">
                      <span className="cursor-help inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold">i</span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-[10px] rounded shadow-lg z-10 pointer-events-none">
                        If left blank, a unique SKU will be generated automatically based on the product name.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                  <input name="sku" value={formData.sku} onChange={handleChange}
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  {skuUniqueError && <div className="text-xs text-red-600 mt-1">{skuUniqueError}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
                  <select name="categoryId" value={formData.categoryId} onChange={handleChange}
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Select a category</option>
                    {categories.map(cat => <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Product Type</label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData(prev => ({ ...prev, productType: v, isMedicine: v === 'medicine' }));
                    }}
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="retail">Retail</option>
                    <option value="food_beverage">F&B</option>
                    <option value="medicine">Medicine</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Unit of Measure</label>
                  <select name="unitOfMeasure" value={formData.unitOfMeasure || 'pcs'} onChange={handleChange}
                    className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="pcs">pcs</option>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="ltr">ltr</option>
                    <option value="ml">ml</option>
                  </select>
                </div>
              </div>

              {/* conditional sections */}
              {formData.productType === 'retail' && (
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Retail Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Weight-based sale</label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" name="weightBased" checked={!!formData.weightBased} onChange={handleCheckboxChange} />
                        Sold by weight
                      </label>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Net Weight</label>
                      <input name="netWeight" type="number" step="0.01" value={formData.netWeight || ''} onChange={handleChange}
                        className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Gross Weight</label>
                      <input name="grossWeight" type="number" step="0.01" value={formData.grossWeight || ''} onChange={handleChange}
                        className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                    </div>
                  </div>
                </div>
              )}

              {/* Food & Beverage specific fields */}
              {formData.productType === 'food_beverage' && (
                <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">F&B Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Is Perishable</label>
                      <div className="mt-1">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" name="isPerishable" checked={!!formData.isPerishable} onChange={handleCheckboxChange} />
                          <span className="text-sm">Perishable</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Shelf Life (days)</label>
                      <input name="shelfLifeDays" type="number" value={formData.shelfLifeDays || ''} onChange={handleChange}
                        className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Storage Temp</label>
                      <input name="storageTemp" value={formData.storageTemp || ''} onChange={handleChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Allergens (comma separated)</label>
                      <input
                        name="allergens"
                        value={(formData.allergens || []).join(', ')}
                        onChange={(e) => setFormData(prev => ({ ...prev, allergens: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                        className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Configurable Attributes */}
              <div className="p-4 border rounded-md space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Configurable Attributes</h3>
                  <button type="button" onClick={handleAddConfigurableAttribute} className="text-sm px-2 py-1 bg-green-600 text-white rounded">+ Add</button>
                </div>
                {formData.configurableAttributes.length === 0 && <div className="text-sm text-gray-500">No configurable attributes.</div>}
                {formData.configurableAttributes.map(attr => (
                  <div key={attr.id} className="p-2 border rounded bg-gray-50">
                    <div className="flex items-center gap-2">
                      <input value={attr.name} onChange={(e) => handleConfigurableAttributeChange(attr.id, 'name', e.target.value)} placeholder="Attribute name" className="flex-grow border rounded p-1" />
                      <button type="button" onClick={() => handleRemoveConfigurableAttribute(attr.id)} className="text-red-600 px-2">Remove</button>
                    </div>
                    <div className="pl-4 mt-2 space-y-2">
                      {attr.values.map(v => (
                        <div key={v.id} className="flex items-center gap-2">
                          <input value={v.value} onChange={(e) => handleAttributeValueChange(attr.id, v.id, e.target.value)} placeholder="Value" className="flex-grow border rounded p-1 text-sm" />
                          <button type="button" onClick={() => handleRemoveAttributeValue(attr.id, v.id)} className="text-red-500 text-sm">Remove</button>
                        </div>
                      ))}
                      <button type="button" onClick={() => handleAddAttributeValue(attr.id)} className="text-xs text-indigo-600">+ Add Value</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Image upload */}
              <div className="border border-gray-100 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase">Product Images</label>
                    <div className="text-xs text-gray-400">Upload multiple images. Mark one as primary.</div>
                  </div>
                </div>

                <div className="mb-3">
                  <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="text-sm text-gray-500" />
                </div>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {formData.images.map(img => (
                      <div key={img.id} className="relative rounded-lg overflow-hidden border border-gray-100 shadow-sm">
                        <img src={img.url} alt="" className="w-full h-32 object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/300x200?text=No+Image'; }} />
                        <div className="p-2 bg-white flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {img.isPrimary && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">Primary</span>}
                            <div className="text-xs text-gray-600">{img.name}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!img.isPrimary && <button type="button" onClick={() => handleSetPrimaryImage(img.id)} className="text-xs text-indigo-600">Make Primary</button>}
                            <button type="button" onClick={() => handleRemoveImage(img.id)} className="text-xs text-red-600">Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Manufactured / Supplier / Marketed */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Manufactured By</label>
                  <select value={formData.manufacturedBy} onChange={(e) => setFormData(prev => ({ ...prev, manufacturedBy: e.target.value }))} className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Select Manufacturer</option>
                    {manufacturers.map(m => <option key={m._id || m.id} value={m._id || m.id}>{m.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Supplier</label>
                  <select value={formData.supplier} onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))} className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Marketed By</label>
                  <select value={formData.marketedBy} onChange={(e) => setFormData(prev => ({ ...prev, marketedBy: e.target.value }))} className="mt-1 block w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Select Marketer</option>
                    {marketers.map(mk => <option key={mk._id || mk.id} value={mk._id || mk.id}>{mk.name}</option>)}
                  </select>
                </div>
              </div>

            </div>
          )}

          {/* BATCHES TAB */}
          {activeTab === 'batches' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-700">Batches / Lots</h3>
                  <div className="text-sm text-gray-500">Manage lot numbers, expiry and quantities for traceability.</div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => { setShowNewBatchForm(true); }} className="px-3 py-1 bg-green-600 text-white rounded-md text-sm">+ New Batch</button>
                  <button type="button" onClick={() => { setActiveTab('product'); }} className="px-3 py-1 border rounded-md text-sm">Back</button>
                </div>
              </div>

              {batchesLoading && <div className="text-sm text-gray-500">Loading batches...</div>}
              {batchesError && <div className="text-sm text-red-600">{batchesError}</div>}

              <div className="space-y-3">
                {(!batchesList || batchesList.length === 0) && !batchesLoading && <div className="text-sm text-gray-500">No batches found for this product.</div>}
                {batchesList.map(b => (
                  <div key={b._id || b.id} className="p-3 border border-gray-100 rounded-lg bg-white grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-500">Lot / Batch</div>
                      <div className="text-sm font-medium text-gray-800">{b.lotNumber || b.batchNumber || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Expiry</div>
                      <div className="text-sm text-gray-800">{b.expiryDate ? new Date(b.expiryDate).toLocaleDateString() : '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Quantity</div>
                      <div className="text-sm text-gray-800">{b.quantity ?? b.qty ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Store</div>
                      <div className="text-sm text-gray-800">{b.store?.name || b.storeId || '—'}</div>
                    </div>
                    <div className="flex justify-end">
                      {/* future: edit/delete actions */}
                    </div>
                  </div>
                ))}
              </div>

              {/* New Batch inline form */}
              {showNewBatchForm && (
                <div className="mt-4 p-4 border border-gray-100 rounded-lg bg-gray-50 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Batch No</label>
                      <input name="batchNumber" value={newBatch.batchNumber} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Quantity</label>
                      <input name="quantity" type="number" min="0" value={newBatch.quantity} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Purchase Price</label>
                      <input name="purchasePrice" type="number" step="0.01" value={newBatch.purchasePrice} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Sell Price</label>
                      <input name="sellPrice" type="number" step="0.01" value={newBatch.sellPrice} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Expiry Date</label>
                      <input name="expiryDate" type="date" value={newBatch.expiryDate} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Store</label>
                      <select name="storeId" value={newBatch.storeId} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm">
                        <option value="">Select store</option>
                        {stores.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name || s.storeName || s.code}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Note</label>
                    <input name="note" value={newBatch.note} onChange={handleNewBatchChange} className="mt-1 block w-full border border-gray-200 rounded-lg p-2 text-sm" />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setShowNewBatchForm(false); setNewBatch({ batchNumber: '', expiryDate: '', quantity: '', purchasePrice: '', sellPrice: '', note: '', storeId: '' }); }} className="px-3 py-1 border rounded-md">Cancel</button>
                    <button type="button" onClick={createBatch} disabled={batchesLoading} className="px-4 py-2 bg-green-600 text-white rounded-md">{batchesLoading ? 'Creating...' : 'Create Batch'}</button>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Actions footer */}
          <div className="mt-6 border-t pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-500">Tip: Use batches to track perishable items and medicines.</div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm">Cancel</button>
              <button type="submit" disabled={loading} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 shadow">
                {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Product')}
              </button>
              {(isEditing || (product && (product._id || product.id))) && (
                <button type="button" onClick={() => setShowStockUpModal(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm">Stock Up</button>
              )}
            </div>
          </div>

        </div>
      </form>

      {/* Modals (use existing Modal component) */}
      <Modal open={showManufacturerForm} onClose={() => setShowManufacturerForm(false)}>
        <h2 className="text-lg font-semibold mb-2">Add Manufacturer</h2>
        <ManufactureForm
          onSave={(newManu) => {
            setManufacturers(prev => [...prev, newManu]);
            setFormData(prev => ({ ...prev, manufacturedBy: newManu._id || newManu.id }));
            setShowManufacturerForm(false);
          }}
        />
      </Modal>

      <Modal open={showSupplierForm} onClose={() => setShowSupplierForm(false)}>
        <h2 className="text-lg font-semibold mb-2">Add Supplier</h2>
        <SupplierForm
          onSave={(newSupp) => {
            setSuppliers(prev => [...prev, newSupp]);
            setFormData(prev => ({ ...prev, supplier: newSupp._id || newSupp.id }));
            setShowSupplierForm(false);
          }}
        />
      </Modal>

      <Modal open={showMarketerForm} onClose={() => setShowMarketerForm(false)}>
        <h2 className="text-lg font-semibold mb-2">Add Marketer</h2>
        <MarketedForm
          onSave={(newMark) => {
            setMarketers(prev => [...prev, newMark]);
            setFormData(prev => ({ ...prev, marketedBy: newMark._id || newMark.id }));
            setShowMarketerForm(false);
          }}
        />
      </Modal>

      <StockUpModal open={showStockUpModal} onClose={() => { setShowStockUpModal(false); setStockUpError(null); }} />

      {showSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[320px] max-w-lg">
            <h3 className="text-lg font-semibold mb-3">Confirm Product Details</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <div><strong>Name:</strong> {formData.name}</div>
              <div><strong>Category:</strong> {categories.find(c => c._id === formData.categoryId || c.id === formData.categoryId)?.name || '—'}</div>
              <div><strong>Price:</strong> {formData.price}</div>
              <div><strong>Stock:</strong> {formData.stock}</div>
              <div className="text-xs text-gray-500">Click Confirm to submit. Server-side validation will also run.</div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowSummary(false)} className="px-3 py-1 border rounded-md">Back</button>
              <button onClick={handleSubmitConfirmed} className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-md">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductForm;